import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button, Icon } from "@sellar-io/components";
import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "../../../components/modal/Dialog";
import type { useOrderDeliveryHistory } from "../../../hooks/api/v2/order/useOrderDeliveryHistory";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { FailedCollectionBuyerActionCard } from "../AcceptedOrders/FailedCollectionBuyerActionCard";
import { WholeOrderExceptionBuyerActionCard } from "../AcceptedOrders/WholeOrderExceptionBuyerActionCard";
import { DeliveryHistoryEntry } from "./DeliveryHistoryEntry";
import { historyDate } from "./deliveryHistoryDates";

type HistoryQuery = ReturnType<typeof useOrderDeliveryHistory>;

export function OrderDeliveryHistory({
  order,
  query,
}: {
  order: OrderGetResponseType;
  query: HistoryQuery;
}) {
  const [open, setOpen] = useState(false);
  const flags = useRemoteFeatureFlags();
  if (order.fulfillmentType !== "sellarDistribution") {
    return null;
  }
  const history = query.data;
  if (!query.isPending && !query.isError && !history?.affected) {
    return null;
  }
  const showDetails = () => {
    setOpen(true);
    query.refetch();
  };
  const buyer = history?.audience === "buyer";
  const canDecide = Boolean(
    buyer &&
    !order.cancelledAt &&
    !order.voidedAt &&
    !order.dispatchedAt &&
    !order.deliveredAt,
  );
  const failedCollection = order.FailedCollectionResolution;
  const hasFailedCollectionAction = Boolean(
    failedCollection &&
    failedCollection.state !== "replanned" &&
    failedCollection.state !== "cancelled",
  );
  const hasDecisionHistory = history?.entries.some(
    (entry) => entry.type === "decision",
  );
  const showWholeOrderDecision =
    canDecide &&
    !hasFailedCollectionAction &&
    (history?.buyerActionRequired || hasDecisionHistory) &&
    flags.isActive("sdWholeOrderExceptionHandling");
  const showDeliveryDate = buyer || Boolean(order.dispatchedAt);
  const currentDate = showDeliveryDate
    ? history?.currentDates.deliveryAt
    : history?.currentDates.collectionAt;
  const latestEstimate = history?.entries.find(
    (entry) => entry.type === "estimate",
  );
  let estimatedDate: Date | null = null;
  if (latestEstimate?.type === "estimate") {
    estimatedDate = showDeliveryDate
      ? latestEstimate.deliveryAt
      : latestEstimate.collectionAt;
  }
  let dateType = "Delivery";
  if (!showDeliveryDate) {
    dateType = order.SupplierPalletShipment ? "Pallet arrival" : "Collection";
  }
  let dateText = `We’ll confirm the updated ${dateType.toLowerCase()} arrangements.`;
  if (currentDate) {
    dateText = `${dateType} scheduled for ${historyDate(currentDate)}.`;
  } else if (estimatedDate) {
    dateText = `Estimated ${dateType.toLowerCase()} date: ${historyDate(estimatedDate)}.`;
  }
  const terminal = order.cancelledAt || order.voidedAt || order.deliveredAt;
  let entryPoint: ReactNode;
  if (query.isPending) {
    entryPoint = (
      <p role="status" className="text-sm text-zinc-500">
        Checking delivery information…
      </p>
    );
  } else if (canDecide && hasFailedCollectionAction && failedCollection) {
    entryPoint = (
      <FailedCollectionBuyerActionCard
        orderId={order.id}
        supplierName={order.Supplier.Business.name}
        resolution={failedCollection}
        onViewDetails={showDetails}
      />
    );
  } else if (showWholeOrderDecision) {
    entryPoint = (
      <WholeOrderExceptionBuyerActionCard
        orderId={order.id}
        onViewDetails={showDetails}
      />
    );
  } else {
    entryPoint = (
      <div className="rounded border border-blue-200 bg-blue-50 p-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
          <Icon icon="INFORMATION_OUTLINE" size={0.75} />
          <p>Important info about delivery</p>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="small" color="secondary" onClick={showDetails}>
            View details
          </Button>
          {query.isError && (
            <Button
              size="small"
              color="secondary"
              disabled={query.isFetching}
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          )}
        </div>
        {query.isError && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            We couldn’t refresh the delivery information. Please try again.
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-3">
      {!terminal && history?.affected && (
        <p className="text-xs text-zinc-600">{dateText}</p>
      )}
      {entryPoint}
      <Dialog
        active={open}
        onDismiss={() => setOpen(false)}
        className="md:max-w-xl"
      >
        <DialogNavigation title="Important info about delivery">
          <button
            type="button"
            aria-label="Close delivery information"
            onClick={() => setOpen(false)}
            className="absolute top-4 left-4 rounded p-1 text-zinc-700 hover:bg-zinc-100 focus-visible:outline-2"
          >
            <Icon icon="CLOSE" size={1} />
          </button>
        </DialogNavigation>
        <DialogContent>
          <HistoryContent query={query} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryContent({ query }: { query: HistoryQuery }) {
  if (query.isError) {
    return (
      <div role="alert" className="space-y-3">
        <p>We couldn’t load the delivery information. Please try again.</p>
        <Button
          size="small"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (query.isPending) {
    return <p role="status">Loading delivery information…</p>;
  }
  const history = query.data;
  if (!history?.entries.length) {
    return <p>No recorded delivery information is available.</p>;
  }
  return (
    <>
      <span role="status" className="sr-only">
        {query.isFetching ? "Refreshing delivery information…" : ""}
      </span>
      <ol aria-label="Delivery history" className="ml-2">
        {history.entries.map((entry) => (
          <DeliveryHistoryEntry
            key={entry.id}
            entry={entry}
            history={history}
          />
        ))}
      </ol>
    </>
  );
}
